import React, { useEffect } from 'react';
import { Navigate, matchPath, useLocation, useNavigate } from 'react-router-dom';
import { ROUTE_KEYS, STORAGE_KEYS } from 'utils/constants';
import { getRoleRoutes, getTeamRoles } from 'utils/role-config';
import useRoleContext from 'contexts/role.context';
import { getDefaultAuthorizedRoute, canAccessRoute } from 'utils/route-authorization';

const AuthRoute = ({ children }: { children: JSX.Element }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { role, permissions, isMainAdmin } = useRoleContext();
  const token: any = localStorage.getItem(STORAGE_KEYS.TOKEN);
  const teamRoutes = getTeamRoles();
  const hasRoleId = Boolean(role?.id);
  const shouldRedirectToLogin = !token || !hasRoleId;

  const accessRoutes = getRoleRoutes();
  const hasLegacyAccess = accessRoutes.some((path) => !!matchPath({ path }, location.pathname));
  const hasPermissionAccess = canAccessRoute(location.pathname, permissions);
  const hasPermissionPayload = permissions.length > 0;
  const fallbackFromRole = role?.name ? teamRoutes?.[role.name]?.baseRoute ?? '' : '';
  const normalizedRoleFallback = fallbackFromRole.startsWith('/') ? fallbackFromRole : '';
  const fallback = hasPermissionPayload
    ? getDefaultAuthorizedRoute(permissions)
    : normalizedRoleFallback || getDefaultAuthorizedRoute(permissions);
  const isFallbackRoute = fallback
    ? location.pathname === fallback || location.pathname.startsWith(`${fallback}/`)
    : false;

  const hasAccess =
    isMainAdmin ||
    (hasPermissionPayload
      ? hasPermissionAccess
      : hasLegacyAccess || (!accessRoutes.length && isFallbackRoute));

  useEffect(() => {
    if (!token || !hasRoleId || shouldRedirectToLogin) {
      return;
    }

    if (!hasAccess && fallback && location.pathname !== fallback) {
      navigate(fallback, { replace: true });
    }
  }, [fallback, hasAccess, hasRoleId, location.pathname, navigate, shouldRedirectToLogin, token]);

  if (!token) {
    return <Navigate to={ROUTE_KEYS.LOGIN} />;
  }

  if (!hasRoleId) {
    localStorage.clear();
    return <Navigate to={ROUTE_KEYS.LOGIN} />;
  }

  if (!hasAccess) {
    return null;
  }

  return children;
};
export default AuthRoute;
